import { useState, useRef, useEffect } from "react";
import { Btn, Field, Card } from "./components.js";
import { TAX_SYSTEMS, CANADA_MODES, getTaxIdLabel, CURRENCIES, fmtCurrency } from "./utils.js";
import {
  uploadImage,
  updateBrandInfo,
  getCurrentBrandInfo
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// SESSION STORAGE OPERATIONS (FOR FREE TIER)
// ══════════════════════════════════════════════════════════════════════════════

const BRAND_STORAGE_KEY = "invoice_gen_brand_session";

/**
 * Get brand from sessionStorage (FREE tier only)
 * @returns {object} Brand object
 */
const getBrandFromSession = () => {
  try {
    const data = sessionStorage.getItem(BRAND_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error("Failed to load brand from sessionStorage:", error);
    return {};
  }
};

/**
 * Save brand to sessionStorage (FREE tier only)
 * @param {object} brand - Brand object to save
 */
const saveBrandToSession = (brand) => {
  try {
    sessionStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(brand));
  } catch (error) {
    console.error("Failed to save brand to sessionStorage:", error);
    
    // Check if quota exceeded
    if (error.name === 'QuotaExceededError') {
      throw new Error("Storage quota exceeded. Please reduce image sizes or remove unused images.");
    }
    throw error;
  }
};

/**
 * Convert File to base64 data URL (FREE tier only)
 * @param {File} file - Image file to convert
 * @returns {Promise<string>} Base64 data URL
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function BrandSetup({ G, brand, setBrand, onDone, settings, user }) {
  // Detect dark mode from G theme colors
  const darkMode = G.bg === "#1C1C1E";
  
  const [local, setLocal] = useState(brand);
  const [originalBrand, setOriginalBrand] = useState(brand);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  
  // Upload error state
  const [uploadError, setUploadError] = useState("");

  // Success/Error states
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [taxIdError, setTaxIdError] = useState("");
  const [addTaxPulse, setAddTaxPulse] = useState(false);
  
  // Image preview states (stores URLs from Supabase Storage OR base64 for FREE tier)
  const [logoPreview, setLogoPreview] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const [watermarkPreview, setWatermarkPreview] = useState("");
  
  const logoRef = useRef();
  const sigRef = useRef();
  const watermarkRef = useRef();

  // Check if user is FREE tier
  const isFreeTier = user?.tierLevel === 0;

  // ══════════════════════════════════════════════════════════════════════════════
  // LOAD FRESH DATA ON MOUNT - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const loadFreshBrand = async () => {
      try {
        if (isFreeTier) {
          // ═══════════════════════════════════════════════════════════════════
          // FREE TIER - Load from sessionStorage
          // ═══════════════════════════════════════════════════════════════════
          
          const sessionBrand = getBrandFromSession();
          setLocal(sessionBrand);
          setOriginalBrand(sessionBrand);
          
          // Set image previews from base64 data
          if (sessionBrand.logoBase64) {
            setLogoPreview(sessionBrand.logoBase64);
          }
          if (sessionBrand.signatureBase64) {
            setSignaturePreview(sessionBrand.signatureBase64);
          }
          if (sessionBrand.watermarkBase64) {
            setWatermarkPreview(sessionBrand.watermarkBase64);
          }
          
        } else {
          // ═══════════════════════════════════════════════════════════════════
          // PAID TIER - Load from database
          // ═══════════════════════════════════════════════════════════════════
          
          // Fetch with hydration (includes image URLs)
          const brandInfo = await getCurrentBrandInfo({ hydrate: true });
          
          if (brandInfo) {
            setLocal(brandInfo);
            setOriginalBrand(brandInfo);
            
            // Set image previews from hydrated data
            if (brandInfo.logo?.url) {
              setLogoPreview(brandInfo.logo.url);
            }
            if (brandInfo.signature?.url) {
              setSignaturePreview(brandInfo.signature.url);
            }
            if (brandInfo.watermarkImage?.url) {
              setWatermarkPreview(brandInfo.watermarkImage.url);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load brand data:", error);
        setSaveError("Failed to load brand data. Please refresh the page.");
      }
    };
    
    loadFreshBrand();
  }, [isFreeTier]); // Re-run if tier changes (unlikely but safe)

  // ══════════════════════════════════════════════════════════════════════════════
  // TRACK CHANGES
  // ══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (isFreeTier) {
      // For FREE tier, compare local with sessionStorage
      const sessionBrand = getBrandFromSession();
      const changed = JSON.stringify(local) !== JSON.stringify(sessionBrand);
      setHasChanges(changed);
    } else {
      // For PAID tier, compare relevant fields (exclude hydrated image data)
      const localClean = { ...local };
      delete localClean.logo;
      delete localClean.signature;
      delete localClean.watermarkImage;
      delete localClean.userId;
      delete localClean.updatedAt;
      
      const brandClean = { ...brand };
      delete brandClean.logo;
      delete brandClean.signature;
      delete brandClean.watermarkImage;
      delete brandClean.userId;
      delete brandClean.updatedAt;
      
      const changed = JSON.stringify(localClean) !== JSON.stringify(brandClean);
      setHasChanges(changed);
    }
  }, [local, brand, isFreeTier]);

  // ══════════════════════════════════════════════════════════════════════════════
  // IMAGE UPLOAD - TIER-BASED (Base64 for FREE, Supabase Storage for PAID)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleImageUpload = (type, idField, previewSetter, loadingSetter) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Clear previous errors
    setUploadError("");
    
    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setUploadError(`Invalid file type (${file.type}). Only JPG and PNG images are allowed.`);
      return;
    }

    // Tier-based file size validation
    const maxSize = isFreeTier ? 1 * 1024 * 1024 : 5 * 1024 * 1024; // 1MB for FREE, 5MB for PAID
    if (file.size > maxSize) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      const maxMB = isFreeTier ? 1 : 5;
      setUploadError(
        `File too large (${sizeMB} MB). Maximum size is ${maxMB} MB for your tier. ` +
        `Please choose a smaller image.`
      );
      return;
    }
    
    loadingSetter(true);
    
    try {
      if (isFreeTier) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Convert to base64 and store in sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        const base64 = await fileToBase64(file);
        
        // Store base64 in local state
        const base64Key = `${type}Base64`;
        setLocal(p => ({ ...p, [base64Key]: base64 }));
        
        // Update preview for UI (base64 works directly in <img src>)
        previewSetter(base64);
        
        // Mark as changed
        setHasChanges(true);
        
        console.log(`✓ ${type} converted to base64`);
        setUploadError("");
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Upload to Supabase Storage
        // ═══════════════════════════════════════════════════════════════════
        
        const storageUrl = await uploadImage(file, type);
        
        // Store URL in local state
        setLocal(p => ({ ...p, [idField]: storageUrl }));
        
        // Update preview for UI
        previewSetter(storageUrl);
        
        // Mark as changed
        setHasChanges(true);
        
        console.log(`✓ ${type} uploaded successfully`);
        setUploadError("");
      }
      
    } catch (error) {
      console.error(`Failed to process ${type}:`, error);
      setUploadError(`Failed to upload ${type}: ${error.message}`);
    } finally {
      loadingSetter(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // DELETE IMAGE HANDLERS - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  const deleteImage = (idField, previewSetter) => () => {
    if (isFreeTier) {
      // FREE tier - clear base64 from local state
      const base64Key = idField.replace('Id', 'Base64'); // logoId -> logoBase64
      setLocal(p => ({ ...p, [base64Key]: null }));
    } else {
      // PAID tier - clear URL from local state
      setLocal(p => ({ ...p, [idField]: null }));
    }
    
    previewSetter("");
    setHasChanges(true);
  };

    // ══════════════════════════════════════════════════════════════════════════════
  // DISCARD HANDLER
  // ══════════════════════════════════════════════════════════════════════════════

  const handleDiscard = () => {
    // Reset to original brand data
    setLocal(originalBrand);
    
    // Reset image previews
    if (isFreeTier) {
      // FREE tier - restore from base64
      if (originalBrand.logoBase64) {
        setLogoPreview(originalBrand.logoBase64);
      } else {
        setLogoPreview("");
      }
      if (originalBrand.signatureBase64) {
        setSignaturePreview(originalBrand.signatureBase64);
      } else {
        setSignaturePreview("");
      }
      if (originalBrand.watermarkBase64) {
        setWatermarkPreview(originalBrand.watermarkBase64);
      } else {
        setWatermarkPreview("");
      }
    } else {
      // PAID tier - restore from hydrated URLs
      if (originalBrand.logo?.url) {
        setLogoPreview(originalBrand.logo.url);
      } else {
        setLogoPreview("");
      }
      if (originalBrand.signature?.url) {
        setSignaturePreview(originalBrand.signature.url);
      } else {
        setSignaturePreview("");
      }
      if (originalBrand.watermarkImage?.url) {
        setWatermarkPreview(originalBrand.watermarkImage.url);
      } else {
        setWatermarkPreview("");
      }
    }
    
    // Clear errors
    setTaxIdError("");
    setSaveError("");
    setUploadError("");
    
    // Reset change tracking
    setHasChanges(false);
    
    console.log("✓ Changes discarded, restored to original brand data");
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // SAVE HANDLER - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  const handleSave = async () => {
    // Validate tax ID
    const idLabel = getTaxIdLabel(local.taxSystem);
    const needsId = local.taxSystem !== "NONE";
    
    if (needsId && !local.taxId) {
      setTaxIdError(`${idLabel} is required. If you don't have one, select "No Tax" as your tax system.`);
      return;
    }
    
    setSaving(true);
    setTaxIdError("");
    setSaveError("");
    setSaveSuccess(false);
    
    try {
      if (isFreeTier) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Save to sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        saveBrandToSession(local);
        
        // Update parent state
        setBrand(local);
        setHasChanges(false);
        setSaveSuccess(true);
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
        
        console.log("✓ Brand info saved to browser session");
        
        // Navigate to next step
        onDone();
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Save to database
        // ═══════════════════════════════════════════════════════════════════
        
        // Clean up hydrated image data before saving
        const dataToSave = { ...local };
        delete dataToSave.logo;
        delete dataToSave.signature;
        delete dataToSave.watermarkImage;
        delete dataToSave.userId;
        delete dataToSave.updatedAt;
        // Also remove base64 keys if any (shouldn't exist for PAID tier, but safety)
        delete dataToSave.logoBase64;
        delete dataToSave.signatureBase64;
        delete dataToSave.watermarkBase64;
        
        // Update brand info in database (UPSERT)
        const updated = await updateBrandInfo(dataToSave);
        
        // Success!
        setSaveSuccess(true);
        
        // Trigger navbar refresh (update logo/name/owner in real-time)
        window.dispatchEvent(new Event('brandUpdated'));
        
        // Update parent state (cache)
        setBrand(updated);
        setHasChanges(false);
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
        
        console.log("✓ Brand info saved successfully");
        
        // Navigate to next step
        onDone();
      }
      
    } catch (error) {
      console.error("Failed to save brand:", error);
      setSaveError(error.message || "Failed to save brand info. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // CUSTOM TAX HANDLERS
  // ══════════════════════════════════════════════════════════════════════════════

  const addCustomTax = () => {
    setLocal(p => ({ ...p, customTaxes: [...(p.customTaxes || []), { name: "", rate: "" }] }));
    setAddTaxPulse(true);
    setTimeout(() => setAddTaxPulse(false), 450);
  };

  const updateCustomTax = (i, field, val) => 
    setLocal(p => ({ ...p, customTaxes: p.customTaxes.map((t, idx) => idx === i ? { ...t, [field]: val } : t) }));
  
  const removeCustomTax = (i) => 
    setLocal(p => ({ ...p, customTaxes: p.customTaxes.filter((_, idx) => idx !== i) }));

  // ══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════════════════════════════

  const getCurrencySymbol = () => {
    if (local.currency === "NONE") return "";
    if (local.currency === "CUSTOM") return local.customCurrencySymbol || "?";
    const currencyObj = CURRENCIES.find(c => c.value === local.currency);
    return currencyObj?.symbol || "";
  };

  const UploadTile = ({ G, label, value, inputRef, onUpload, onDelete, width, height, placeholder, loading }) => (
    <div style={{ textAlign: "center" }}>
      <div
        onClick={() => !loading && inputRef.current.click()}
        title={loading ? "Uploading..." : `Upload ${label}`}
        style={{
          width, height,
          borderRadius: 10,
          border: `2px dashed ${value ? G.accent : G.border}`,
          background: value ? G.accentBg : G.surface2,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: loading ? "wait" : "pointer", 
          overflow: "hidden", 
          margin: "0 auto 8px",
          transition: "border-color .15s, background .15s",
          position: "relative"
        }}
      >
        {loading ? (
          <div style={{ fontSize: 24 }}>⏳</div>
        ) : value ? (
          <img src={value} alt={label} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 0 }} />
        ) : (
          <span style={{ fontSize: 12, color: G.muted }}>{placeholder}</span>
        )}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
        {loading ? "Uploading..." : label}
      </div>
      {!loading && (
        <div style={{ 
          fontSize: 12, 
          color: G.muted, 
          lineHeight: 1.3,
          marginTop: 12,
          marginBottom: 4
        }}>
          JPG, PNG<br/>Max {isFreeTier ? "1" : "5"} MB
        </div>
      )}
      {value && !loading && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{
            fontSize: 12,
            color: G.danger,
            background: "transparent",
            border: 0,
            cursor: "pointer",
            fontFamily: "'Sora', sans-serif",
            padding: 10
          }}
          title="Delete"
        >
          🗑 Delete
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }} onChange={onUpload} />
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Page Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
            🏢 Brand Setup
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            {hasChanges ? (
              <span style={{ color: G.accent, fontWeight: 500 }}>● Unsaved changes</span>
            ) : saveSuccess ? (
              <span style={{ color: G.success, fontWeight: 500 }}>✓ Saved successfully</span>
            ) : (
              "Configure your business details for invoices"
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {saveError && (
        <div style={{
          background: G.dangerBg,
          border: `1px solid ${G.danger}`,
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 20,
          color: G.danger,
          fontSize: 13,
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span>{saveError}</span>
        </div>
      )}

      {/* Business Identity */}
      <Card G={G} style={{ 
        padding: 28, 
        marginBottom: 20,
        border: darkMode ? `1px solid ${G.border}` : undefined 
      }}>
        <div style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
          Business Identity
        </div>
        <div style={{ fontSize: 12, color: G.ink2, marginBottom: 20 }}>
          Your logo and signature appear on every invoice
        </div>

        {/* Upload Error Message */}
        {uploadError && (
          <div style={{
            background: G.dangerBg,
            border: `1px solid ${G.danger}`,
            borderRadius: 7,
            padding: "10px 14px",
            fontSize: 12,
            color: G.danger,
            marginBottom: 16,
            display: "flex",
            alignItems: "flex-start",
            gap: 10
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Upload Failed</div>
              <div>{uploadError}</div>
            </div>
            <button
              onClick={() => setUploadError("")}
              style={{
                background: "transparent",
                border: "none",
                color: G.danger,
                cursor: "pointer",
                fontSize: 18,
                fontWeight: 700,
                padding: 0,
                lineHeight: 1,
                flexShrink: 0
              }}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
          {/* Name / Owner / Tagline */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Business Name" G={G}>
                <input
                  value={local.name || ""}
                  onChange={e => setLocal(p => ({ ...p, name: e.target.value.slice(0, 50) }))}
                  placeholder="e.g. Ace Solutions"
                  maxLength={50}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.name || "").length}/50 characters</div>
              </Field>
              <Field label="Owner Name" G={G}>
                <input
                  value={local.owner || ""}
                  onChange={e => setLocal(p => ({ ...p, owner: e.target.value.slice(0, 50) }))}
                  placeholder="e.g. Rahul Sharma"
                  maxLength={50}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.owner || "").length}/50 characters</div>
              </Field>
            </div>

            <Field label="Tagline (optional)" G={G}>
              <input
                value={local.tagline || ""}
                onChange={e => setLocal(p => ({ ...p, tagline: e.target.value.slice(0, 80) }))}
                placeholder="e.g. Professional Services and Solutions Provider"
                maxLength={80}
              />
              <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.tagline || "").length}/80 characters</div>
            </Field>
          </div>

          {/* Vertical divider */}
          <div style={{ width: 1, alignSelf: "stretch", background: G.border, flexShrink: 0 }} />

          {/* Logo + Signature (Right-aligned) */}
          <div style={{ display: "flex", gap: 24, flexShrink: 0 }}>
            <UploadTile
              G={G}
              label="Logo"
              value={logoPreview}
              inputRef={logoRef}
              onUpload={handleImageUpload("logo", "logoId", setLogoPreview, setUploadingLogo)}
              onDelete={deleteImage("logoId", setLogoPreview)}
              width={96}
              height={96}
              placeholder="+ Logo"
              loading={uploadingLogo}
            />
            <UploadTile
              G={G}
              label="Signature"
              value={signaturePreview}
              inputRef={sigRef}
              onUpload={handleImageUpload("signature", "signatureId", setSignaturePreview, setUploadingSignature)}
              onDelete={deleteImage("signatureId", setSignaturePreview)}
              width={140}
              height={96}
              placeholder="+ Signature"
              loading={uploadingSignature}
            />
          </div>
        </div>
      </Card>

      {/* Contact & Address */}
      <Card G={G} style={{ 
        marginBottom: 16,
        border: darkMode ? `1px solid ${G.border}` : undefined 
      }}>
        <div style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
          Contact & Address
        </div>
        <div style={{ fontSize: 12, color: G.ink2, marginBottom: 18 }}>
          Contact information and business location
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label="Phone" G={G}>
            <input 
              value={local.phone || ""} 
              onChange={e => {
                const value = e.target.value;
                if (/^[0-9+\s\-()]*$/.test(value)) {
                  setLocal(p => ({ ...p, phone: value.slice(0, 20) }));
                }
              }}
              placeholder="+1 234 567 8901"
              maxLength={20}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.phone || "").length}/20 characters</div>
          </Field>

          <Field label="Email" G={G}>
            <input 
              type="email" 
              value={local.email || ""} 
              onChange={e => setLocal(p => ({ ...p, email: e.target.value.slice(0, 50) }))} 
              placeholder="contact@acesolutions.com"
              maxLength={50}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.email || "").length}/50 characters</div>
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label="Address Line 1" G={G}>
            <input 
              value={local.addressLine1 || ""} 
              onChange={e => setLocal(p => ({ ...p, addressLine1: e.target.value.slice(0, 100) }))} 
              placeholder="Shop / Office / Suite Number, Building / Apartment, Street"
              maxLength={100}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.addressLine1 || "").length}/100 characters</div>
          </Field>

          <Field label="Address Line 2" G={G}>
            <input 
              value={local.addressLine2 || ""} 
              onChange={e => setLocal(p => ({ ...p, addressLine2: e.target.value.slice(0, 100) }))} 
              placeholder="Area / Locality, Landmark"
              maxLength={100}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.addressLine2 || "").length}/100 characters</div>
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <Field label="City" G={G}>
            <input 
              value={local.city || ""} 
              onChange={e => setLocal(p => ({ ...p, city: e.target.value.slice(0, 60) }))} 
              placeholder=""
              maxLength={60}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.city || "").length}/60</div>
          </Field>

          <Field label="State / Province" G={G}>
            <input 
              value={local.state || ""} 
              onChange={e => setLocal(p => ({ ...p, state: e.target.value.slice(0, 50) }))} 
              placeholder=""
              maxLength={50}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.state || "").length}/50</div>
          </Field>

          <Field label="ZIP / Postal / PIN" G={G}>
            <input 
              value={local.zip || ""} 
              onChange={e => setLocal(p => ({ ...p, zip: e.target.value.slice(0, 12) }))} 
              placeholder=""
              maxLength={12}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.zip || "").length}/12</div>
          </Field>

          <Field label="Country" G={G}>
            <input 
              value={local.country || ""} 
              onChange={e => setLocal(p => ({ ...p, country: e.target.value.slice(0, 50) }))} 
              placeholder=""
              maxLength={50}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.country || "").length}/50</div>
          </Field>
        </div>
      </Card>

      {/* Tax & Currency - Split into two equal cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Tax System Card */}
        <Card G={G} style={{ border: darkMode ? `1px solid ${G.border}` : undefined }}>
          <div style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
            Tax System
          </div>
          <div style={{ fontSize: 12, color: G.ink2, marginBottom: 18 }}>
            Select your regional tax configuration
          </div>

          <Field label="Tax System" G={G}>
            <select 
              value={local.taxSystem || "GST_INDIA"} 
              onChange={e => setLocal(p => ({ ...p, taxSystem: e.target.value, taxId: "", canadaMode: "GST_PST", customTaxes: [] }))}
            >
              {TAX_SYSTEMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>

          {local.taxSystem === "CANADA" && (
            <Field label="Province Tax Type" G={G}>
              <select value={local.canadaMode || "GST_PST"} onChange={e => setLocal(p => ({ ...p, canadaMode: e.target.value }))}>
                {CANADA_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          )}

          {local.taxSystem === "CUSTOM" && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: G.ink2, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Custom Tax Fields
                </label>
                <Btn 
                  size="xs" 
                  onClick={addCustomTax} 
                  disabled={(local.customTaxes || []).length >= 2}
                  className={addTaxPulse ? "btn-pulse" : ""} 
                  style={{ 
                    background: (local.customTaxes || []).length >= 2 ? G.surface2 : G.accent, 
                    color: (local.customTaxes || []).length >= 2 ? G.muted : G.accentFg,
                    cursor: (local.customTaxes || []).length >= 2 ? "not-allowed" : "pointer",
                    opacity: (local.customTaxes || []).length >= 2 ? 0.5 : 1
                  }}
                >
                  + Add Tax {(local.customTaxes || []).length >= 2 ? "(Max 2)" : ""}
                </Btn>
              </div>

              {(local.customTaxes || []).map((t, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <input 
                    value={t.name} 
                    onChange={e => updateCustomTax(i, "name", e.target.value)} 
                    placeholder="Tax name" 
                    maxLength={20} 
                  />
                  <input 
                    type="number" 
                    value={t.rate} 
                    onChange={e => updateCustomTax(i, "rate", e.target.value)} 
                    placeholder="Rate %" 
                    maxLength={6} 
                  />
                  <Btn 
                    size="xs" 
                    onClick={() => removeCustomTax(i)} 
                    style={{ background: G.dangerBg, color: G.danger, border: "none" }}
                  >
                    ✕
                  </Btn>
                </div>
              ))}
            </div>
          )}

          {local.taxSystem !== "NONE" && (
            <Field label={`${getTaxIdLabel(local.taxSystem)} *`} error={taxIdError} G={G}>
              <input
                value={local.taxId || ""}
                onChange={e => { setLocal(p => ({ ...p, taxId: e.target.value.slice(0, 20) })); setTaxIdError(""); }}
                placeholder={`Enter your ${getTaxIdLabel(local.taxSystem)}`}
                maxLength={20}
                style={{ borderColor: taxIdError ? "#C53030" : undefined }}
              />
              <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.taxId || "").length}/20 characters</div>
            </Field>
          )}
          {taxIdError && (
            <div style={{ background: G.dangerBg, border: `1px solid ${G.danger}`, borderRadius: 7, padding: "10px 14px", fontSize: 12, color: G.danger, marginTop: 4 }}>
              {taxIdError}
            </div>
          )}
        </Card>

        {/* Currency Card */}
        <Card G={G} style={{ border: darkMode ? `1px solid ${G.border}` : undefined }}>
          <div style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
            Currency
          </div>
          <div style={{ fontSize: 12, color: G.ink2, marginBottom: 18 }}>
            Choose your invoice currency
          </div>

          <Field label="Currency" G={G}>
            <select 
              value={local.currency || "NONE"} 
              onChange={e => setLocal(p => ({ ...p, currency: e.target.value, customCurrencySymbol: "" }))}
            >
              {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>

          {local.currency === "CUSTOM" && (
            <Field label="Custom Currency Symbol" G={G}>
              <input 
                value={local.customCurrencySymbol || ""} 
                onChange={e => setLocal(p => ({ ...p, customCurrencySymbol: e.target.value.slice(0, 5) }))} 
                placeholder="e.g. kr, ¥, Fr" 
                maxLength={5}
              />
              <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.customCurrencySymbol || "").length}/5 characters</div>
            </Field>
          )}

          <div style={{ 
            marginTop: 12, 
            padding: 14, 
            background: G.accentBg, 
            borderRadius: 8, 
            border: `1px solid ${G.accent}44`,
            display: "flex",
            gap: 20,
            alignItems: "flex-start"
          }}>
            {/* Left Column - Preview */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: G.accent, marginBottom: 8, fontWeight: 600 }}>
                Preview (based on your Settings):
              </div>
              <div 
                dir="ltr"
                style={{ 
                  fontWeight: 700, 
                  color: G.accent, 
                  fontSize: 16,
                  direction: "ltr",
                  textAlign: "left",
                  unicodeBidi: "isolate"
                }}
              >
                {fmtCurrency(
                  123456789.50,
                  getCurrencySymbol(),
                  settings?.currencyFormat || "symbol-before-space",
                  local.currency || "NONE",
                  settings?.amountGrouping || "international",
                  settings?.amountSeparator || "comma-dot",
                  settings?.amountDecimals !== false
                )}
              </div>
            </div>

            {/* Right Column - Settings Details */}
            <div style={{ flex: 1, fontSize: 11, color: G.muted, lineHeight: 1.6 }}>
              <div>Raw value: 123,456,789.50</div>
              <div>Grouping: {settings?.amountGrouping || "international"}</div>
              <div>Separator: {settings?.amountSeparator || "comma-dot"}</div>
              <div>Decimals: {settings?.amountDecimals !== false ? "shown" : "hidden"}</div>
              <div>Format: {settings?.currencyFormat || "symbol-before-space"}</div>
            </div>
          </div>
          
          {(settings?.currencyFormat || "symbol-before-space").includes("before") && (
            <div style={{ 
              background: darkMode ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2",
              border: darkMode ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: 8, 
              padding: "8px 12px", 
              fontSize: 12, 
              color: darkMode ? "#FCA5A5" : "#991B1B",
              marginTop: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
              lineHeight: 1.4
            }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
              <span>
                Notice: Some languages which read from right-to-left (e.g. Arabic, Persian, Hebrew, Urdu) may not render currency display format correctly with "symbol/code before amount" formats. For accurate display, use "symbol after" or "no symbol" option from settings.
              </span>
            </div>
          )}
        </Card>
      </div>

      {/* Watermark */}
      <Card G={G} style={{ 
        marginBottom: 16,
        border: darkMode ? `1px solid ${G.border}` : undefined 
      }}>
        <div style={{ fontFamily: "'Lora', serif", fontSize: 16, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
          Invoice Watermark
        </div>
        <div style={{ fontSize: 12, color: G.ink2, marginBottom: 18 }}>
          Add your brand identity as a background watermark on every invoice
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            ["none", "No Watermark"],
            ["text", "Text Watermark"],
            ["image", "Image/Logo Watermark"]
          ].map(([type, label]) => (
            <button
              key={type}
              onClick={() => setLocal(p => ({ ...p, watermarkType: type }))}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: `1.5px solid ${local.watermarkType === type ? G.accent : G.border}`,
                borderRadius: 7,
                background: local.watermarkType === type ? G.accentBg : G.surface,
                color: local.watermarkType === type ? G.accent : G.ink2,
                fontSize: 13,
                fontWeight: local.watermarkType === type ? 600 : 500,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                transition: "all .15s"
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {local.watermarkType === "text" && (
          <Field label="Watermark Text" G={G}>
            <input
              value={local.watermarkText || ""}
              onChange={e => setLocal(p => ({ ...p, watermarkText: e.target.value.slice(0, 60) }))}
              placeholder="e.g. Your Company Name, Tagline, etc."
              maxLength={60}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>{(local.watermarkText || "").length}/60 characters</div>
          </Field>
        )}

        {local.watermarkType === "image" && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: G.ink2, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 8 }}>
              Watermark Image/Logo
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                onClick={() => !uploadingWatermark && watermarkRef.current.click()}
                style={{
                  width: 140,
                  height: 100,
                  borderRadius: 10,
                  border: `2px dashed ${G.border}`,
                  background: G.surface,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: uploadingWatermark ? "wait" : "pointer",
                  overflow: "hidden",
                  flexShrink: 0
                }}
              >
                {uploadingWatermark ? (
                  <div style={{ fontSize: 24 }}>⏳</div>
                ) : watermarkPreview ? (
                  <img src={watermarkPreview} alt="watermark" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <span style={{ fontSize: 12, color: G.muted }}>+ Upload</span>
                )}
              </div>
              <div>
                <Btn 
                  size="sm" 
                  onClick={() => watermarkRef.current.click()} 
                  disabled={uploadingWatermark}
                  style={{ 
                    background: uploadingWatermark ? G.surface2 : G.surface2, 
                    color: uploadingWatermark ? G.muted : G.ink, 
                    border: `1px solid ${G.border}`,
                    cursor: uploadingWatermark ? "wait" : "pointer",
                    marginBottom: 8
                  }}
                >
                  {uploadingWatermark ? "Uploading..." : "Upload Image"}
                </Btn>
                <div style={{ fontSize: 12, color: G.ink2, marginBottom: 6, lineHeight: 1.4 }}>
                  📎 <strong>JPG or PNG</strong> • Max <strong>{isFreeTier ? "1 MB" : "5 MB"}</strong>
                </div>
                <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.4 }}>
                  💡 Tip: Use PNG with transparency for best results
                </div>
              </div>
              <input
                ref={watermarkRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: "none" }}
                onChange={handleImageUpload("watermark", "watermarkImageId", setWatermarkPreview, setUploadingWatermark)}
              />
            </div>
          </div>
        )}
      </Card>

      {/* Bottom Save Button */}
      <div
        style={{
          position: "sticky",
          bottom: hasChanges ? 20 : 0,
          background: hasChanges ? G.accentBg : G.bg,
          border: hasChanges ? `1px solid ${G.accent}44` : `1px solid transparent`,
          borderRadius: 10,
          padding: hasChanges ? "14px 20px" : "8px 20px",
          marginTop: hasChanges ? 20 : 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: hasChanges ? 8 : 0,
          transition: "all .2s",
          boxShadow: hasChanges ? "0 -4px 12px rgba(0,0,0,0.1)" : "none",
        }}
      >
        {hasChanges && (
          <>
            <div
              style={{
                fontSize: 12,
                color: G.accent,
                fontWeight: 500,
                marginRight: "auto",
              }}
            >
              ● You have unsaved changes
            </div>
            <button
              onClick={handleDiscard}
              disabled={saving}
              style={{
                padding: "10px 24px",
                border: `1.8px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface,
                color: G.ink,
                fontSize: 14,
                fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
                transition: "all .15s",
                opacity: saving ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.target.style.background = G.ink;
                  e.target.style.color = G.surface;
                  e.target.style.border = "1.8px solid transparent";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = G.surface;
                e.target.style.color = G.ink;
                e.target.style.border = `1.8px solid ${G.border}`;
              }}
            >
              Discard
            </button>
          </>
        )}

        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            padding: "10px 24px",
            border: hasChanges
              ? `1.8px solid ${G.accent}40`
              : `1.5px solid ${G.border}`,
            borderRadius: 7,
            background: hasChanges ? G.accentBg : G.surface2,
            color: hasChanges ? G.accent : G.muted,
            fontSize: 13,
            fontWeight: 600,
            cursor: hasChanges && !saving ? "pointer" : "not-allowed",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
          }}
          onMouseEnter={(e) => {
            if (hasChanges && !saving) {
              e.target.style.background = G.accent;
              e.target.style.color = G.accentFg;
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={(e) => {
            if (hasChanges) {
              e.target.style.background = G.accentBg;
              e.target.style.color = G.accent;
              e.target.style.border = `1.8px solid ${G.accent}40`;
            }
          }}
        >
          {saving ? "Saving..." : (hasChanges ? "💾 Save Brand Info" : "✓ All Saved")}
        </button>
      </div>
    </div>
  );
}